import { hasFinishedTraining } from '@lms/shared'
import { Calendar, Clock, ExternalLink, MapPin, Tag, Users } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useEnrollSelf, useMyEnrollments } from '@/hooks/use-training-enrollments'
import { useTrainingSurvey } from '@/hooks/use-training-surveys'
import { useTraining } from '@/hooks/use-trainings'
import { isSoldOut } from '@/lib/training-list'

export default function TrainingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: training, isLoading } = useTraining(id ?? '')
  const { data: myEnrollments } = useMyEnrollments()
  const { data: survey } = useTrainingSurvey(id ?? '')
  const enrollSelf = useEnrollSelf()

  const t = training as any
  const enrollments = Array.isArray(myEnrollments) ? myEnrollments : []
  const myEnrollment = enrollments.find((e: any) => e.trainingId === id)
  const surveyData = survey as any
  const soldOut = isSoldOut(t?.remainingSeats)

  const handleEnroll = async () => {
    if (!id) return
    try {
      await enrollSelf.mutateAsync({ trainingId: id })
      toast.success('申込が完了しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '申込に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
      </div>
    )
  }

  if (!t) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">研修が見つかりません</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="flex items-start gap-3">
          <h1 className="text-2xl font-bold">{t.title}</h1>
          <Badge variant={t.trainingType === 'open' ? 'secondary' : 'default'}>
            {t.trainingType === 'open' ? '募集型' : 'アサイン型'}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {t.category && (
            <Badge variant="outline">
              <Tag className="mr-1 h-3 w-3" />
              {t.category}
            </Badge>
          )}
          {t.tags?.map((tag: string) => (
            <Badge key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      {t.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">説明</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{t.description}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        {t.capacity != null && (
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>
              定員: {t.capacity}名{t.remainingSeats != null && `（残席 ${t.remainingSeats}）`}
            </span>
            {soldOut && <Badge variant="destructive">満席</Badge>}
          </div>
        )}
        {t.standardDurationHours != null && (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{t.standardDurationHours}時間</span>
          </div>
        )}
        {t.hrContactEmail && <span>問い合わせ: {t.hrContactEmail}</span>}
      </div>

      {t.guideFileUrl && (
        <a
          href={t.guideFileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline"
        >
          <ExternalLink className="h-4 w-4" />
          研修ガイドPDF
        </a>
      )}

      {t.schedules?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">日程</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Day</TableHead>
                  <TableHead>日時</TableHead>
                  <TableHead>終了</TableHead>
                  <TableHead>場所</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.schedules.map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell>Day {s.dayNumber}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(s.scheduleDate).toLocaleString('ja-JP')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {s.endDate ? new Date(s.endDate).toLocaleString('ja-JP') : '-'}
                    </TableCell>
                    <TableCell>
                      {s.location ? (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {s.location}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {myEnrollment ? (
            <div className="flex items-center gap-3">
              <span className="text-sm">申込状況:</span>
              <StatusBadge type="enrollment" status={myEnrollment.status} />
            </div>
          ) : t.trainingType === 'open' ? (
            <Button
              onClick={handleEnroll}
              disabled={enrollSelf.isPending || soldOut}
              className="w-full"
            >
              {enrollSelf.isPending
                ? '申込中...'
                : soldOut
                  ? '満席のため申込できません'
                  : 'この研修に申し込む'}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              この研修はアサイン型です。管理者からのアサインをお待ちください。
            </p>
          )}
        </CardContent>
      </Card>

      {surveyData?.formsUrl && myEnrollment && hasFinishedTraining(myEnrollment.status) && (
        <Card>
          <CardContent className="pt-6">
            <a
              href={surveyData.formsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              アンケートに回答する
            </a>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
